import { useMemo, useCallback } from 'react';
import { useRouter } from 'next/router';
import { useAtom } from 'jotai';
import { useIntercom } from 'react-use-intercom';
import { useTranslation } from 'next-i18next';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

import { BaseModal, ContactRestworldManager, ButtonV2 } from '@restworld/ui-ds';

import { showContactRestWorldManagerModalAtom } from '../components/restaurants/employer-restaurants-filters/employer-restaurants.atoms';
import useShowOrganisationQuery from '../components/organisations/hooks/use-show-organisation-query';
import { useJobPositionDetails } from '../components/job-listing/hooks/use-employer-job-position-details';

function ContactRestworldManagerModal() {
  const router = useRouter();
  const { t: translateCommon } = useTranslation('common');
  const [showContactRestworldManagerModal, setShowContactRestworldManagerModal] = useAtom(
    showContactRestWorldManagerModalAtom
  );
  const { showMessages } = useIntercom();
  const { organisationDetails } = useShowOrganisationQuery({ context: 'contact_rw_manager', staleTime: Infinity });
  const jobPositionDetails = useJobPositionDetails();
  const isKanbanRoute = useMemo(() => router.pathname === '/employer/job-positions/[id]', [router.pathname]);

  const { name, surname, icon_url, email, phone_number } = useMemo(() => {
    if (organisationDetails && organisationDetails?.assigned_to_rw_member) {
      const {
        assigned_to_rw_member: {
          name,
          surname,
          icon_url,
          user: { email, phone_number },
        },
      } = organisationDetails;
      return { name, surname, icon_url, email, phone_number };
    } else {
      return {};
    }
  }, [organisationDetails]);

  const { emailFromJp, iconUrlFromJp, nameFromJp, phoneFromJp, surnameFromJp } = useMemo(() => {
    if (jobPositionDetails && jobPositionDetails?.assigned_to_user) {
      const {
        assigned_to_user: {
          name,
          surname,
          icon_url,
          user: { email, phone_number },
        },
      } = jobPositionDetails;

      return {
        nameFromJp: name,
        surnameFromJp: surname,
        iconUrlFromJp: icon_url,
        emailFromJp: email,
        phoneFromJp: phone_number,
      };
    } else {
      return {};
    }
  }, [jobPositionDetails]);

  const rwManager = useMemo(() => {
    if (isKanbanRoute) {
      return {
        email: emailFromJp,
        firstName: nameFromJp,
        image: iconUrlFromJp,
        lastName: surnameFromJp,
        phone: phoneFromJp,
      };
    } else {
      return {
        email,
        firstName: name,
        image: icon_url,
        lastName: surname,
        phone: phone_number,
      };
    }
  }, [
    email,
    emailFromJp,
    iconUrlFromJp,
    icon_url,
    isKanbanRoute,
    name,
    nameFromJp,
    phoneFromJp,
    phone_number,
    surname,
    surnameFromJp,
  ]);

  const handleCloseModal = useCallback(() => {
    setShowContactRestworldManagerModal(false);
  }, [setShowContactRestworldManagerModal]);

  return (
    <BaseModal
      onClose={handleCloseModal}
      open={showContactRestworldManagerModal}
      footer={
        <ButtonV2 fullwidth StartIcon={ChatBubbleLeftRightIcon} onClick={showMessages}>
          {translateCommon('open_live_chat')}
        </ButtonV2>
      }
    >
      <ContactRestworldManager rwManager={rwManager} />
    </BaseModal>
  );
}
export default ContactRestworldManagerModal;
